import type { ReactNode } from "react";
import React from "react";
import { AppConfigContext } from "../app/appConfig";
import { useAlert } from "../app/notificationToast";
import { BillingModel } from "../../api/billing/BillingModel";
import { BillingAdapter } from "../../api/billing/BillingAdapter";
import type {
  Prestation,
  PrestationRequest,
  Product,
  ProductRequest,
  CustomerProduct,
  CounterMovement,
  PurchaseRequest,
  BatchConsumeRequest,
  BatchConsumeResult,
  AdjustRequest,
} from "../../api/billing/Billing.typed";

interface BillingContextType {
  listGroups: (entity: "prestations" | "products") => Promise<string[] | undefined>;
  listPrestations: () => Promise<Prestation[] | undefined>;
  createPrestation: (p: PrestationRequest) => Promise<Prestation | undefined>;
  updatePrestation: (id: number, p: PrestationRequest) => Promise<Prestation | undefined>;
  deletePrestation: (id: number) => Promise<boolean | undefined>;
  listProducts: () => Promise<Product[] | undefined>;
  createProduct: (p: ProductRequest) => Promise<Product | undefined>;
  updateProduct: (id: number, p: ProductRequest) => Promise<Product | undefined>;
  deleteProduct: (id: number) => Promise<boolean | undefined>;
  listCustomerCounters: (
    customerId: number,
    status?: string,
  ) => Promise<CustomerProduct[] | undefined>;
  purchaseProduct: (p: PurchaseRequest) => Promise<CustomerProduct | undefined>;
  batchConsume: (p: BatchConsumeRequest) => Promise<BatchConsumeResult[] | undefined>;
  adjustCounter: (id: number, p: AdjustRequest) => Promise<CustomerProduct | undefined>;
  cancelCounter: (id: number) => Promise<boolean | undefined>;
  listCounterMovements: (id: number) => Promise<CounterMovement[] | undefined>;
}

const BillingContext = React.createContext<BillingContextType>(null!);

export function BillingProvider(props: { children: ReactNode }) {
  const appConfig = React.useContext(AppConfigContext);
  const { showAlert } = useAlert();

  const adapter = React.useMemo(() => {
    if (!appConfig) return null;
    const model = new BillingModel(appConfig);
    return new BillingAdapter(model);
  }, [appConfig]);

  const wrap = React.useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
      try {
        return await fn();
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "Erreur inconnue";
        showAlert(msg, "danger");
        return undefined;
      }
    },
    [showAlert],
  );

  const listGroups = React.useCallback(
    async (entity: "prestations" | "products") => {
      if (!adapter) return undefined;
      return wrap(() => adapter.listGroups(entity));
    },
    [adapter, wrap],
  );

  const listPrestations = React.useCallback(async () => {
    if (!adapter) return undefined;
    return wrap(() => adapter.listPrestations());
  }, [adapter, wrap]);

  const createPrestation = React.useCallback(
    async (p: PrestationRequest) => {
      if (!adapter) return undefined;
      const result = await wrap(() => adapter.createPrestation(p));
      if (result) showAlert("Prestation créée", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const updatePrestation = React.useCallback(
    async (id: number, p: PrestationRequest) => {
      if (!adapter) return undefined;
      const result = await wrap(() => adapter.updatePrestation(id, p));
      if (result) showAlert("Prestation mise à jour", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const deletePrestation = React.useCallback(
    async (id: number) => {
      if (!adapter) return undefined;
      const result = await wrap(async () => {
        await adapter.deletePrestation(id);
        return true;
      });
      if (result) showAlert("Prestation supprimée", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const listProducts = React.useCallback(async () => {
    if (!adapter) return undefined;
    return wrap(() => adapter.listProducts());
  }, [adapter, wrap]);

  const createProduct = React.useCallback(
    async (p: ProductRequest) => {
      if (!adapter) return undefined;
      const result = await wrap(() => adapter.createProduct(p));
      if (result) showAlert("Produit créé", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const updateProduct = React.useCallback(
    async (id: number, p: ProductRequest) => {
      if (!adapter) return undefined;
      const result = await wrap(() => adapter.updateProduct(id, p));
      if (result) showAlert("Produit mis à jour", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const deleteProduct = React.useCallback(
    async (id: number) => {
      if (!adapter) return undefined;
      const result = await wrap(async () => {
        await adapter.deleteProduct(id);
        return true;
      });
      if (result) showAlert("Produit supprimé", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const listCustomerCounters = React.useCallback(
    async (customerId: number, status?: string) => {
      if (!adapter) return undefined;
      return wrap(() => adapter.listCustomerCounters(customerId, status));
    },
    [adapter, wrap],
  );

  const purchaseProduct = React.useCallback(
    async (p: PurchaseRequest) => {
      if (!adapter) return undefined;
      const result = await wrap(() => adapter.purchaseProduct(p));
      if (result) showAlert("Produit acheté", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const batchConsume = React.useCallback(
    async (p: BatchConsumeRequest) => {
      if (!adapter) return undefined;
      return wrap(() => adapter.batchConsume(p));
    },
    [adapter, wrap],
  );

  const adjustCounter = React.useCallback(
    async (id: number, p: AdjustRequest) => {
      if (!adapter) return undefined;
      const result = await wrap(() => adapter.adjustCounter(id, p));
      if (result) showAlert("Compteur ajusté", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const cancelCounter = React.useCallback(
    async (id: number) => {
      if (!adapter) return undefined;
      const result = await wrap(async () => {
        await adapter.cancelCounter(id);
        return true;
      });
      if (result) showAlert("Compteur annulé", "success");
      return result;
    },
    [adapter, wrap, showAlert],
  );

  const listCounterMovements = React.useCallback(
    async (id: number) => {
      if (!adapter) return undefined;
      return wrap(() => adapter.listCounterMovements(id));
    },
    [adapter, wrap],
  );

  const value = React.useMemo<BillingContextType>(
    () => ({
      listGroups,
      listPrestations,
      createPrestation,
      updatePrestation,
      deletePrestation,
      listProducts,
      createProduct,
      updateProduct,
      deleteProduct,
      listCustomerCounters,
      purchaseProduct,
      batchConsume,
      adjustCounter,
      cancelCounter,
      listCounterMovements,
    }),
    [
      listGroups,
      listPrestations,
      createPrestation,
      updatePrestation,
      deletePrestation,
      listProducts,
      createProduct,
      updateProduct,
      deleteProduct,
      listCustomerCounters,
      purchaseProduct,
      batchConsume,
      adjustCounter,
      cancelCounter,
      listCounterMovements,
    ],
  );

  return <BillingContext.Provider value={value}>{props.children}</BillingContext.Provider>;
}

export function useBilling(): BillingContextType {
  const ctx = React.useContext(BillingContext);
  if (!ctx) throw new Error("useBilling must be used within BillingProvider");
  return ctx;
}
